// PARTIES (UI rebuild batch 2) — find the person or business to work with. Astra's direction, adapted to the Books:
//
//   · ONE searchable register (kit Register): a table from lg, a card per party on the phone. Pinning changes the ORDER
//     (pinned first), never the row's anatomy — the pinned cards and the second list of today's screen are one list.
//   · Name over "Pinned · …", Customer / Merchant, a Status pill only when there is one, the chevron — and the Balance
//     column ALWAYS (operator, 26 Oct: "astra looks cleaner on party page with money visible"): the list draws from the
//     fast read at once, and the balances are read in the background with the same query and key the old Show balances
//     switch used (['parties', type, q, true]). While that read is on its way a cell shows "…", never 0; if it fails or
//     answers `balances_read:false` (readState balances) every cell is "—" and one quiet line offers Try again.
//   · A cell is Astra's figure — the amount and its asset, right-aligned — with "owes you" / "you hold" muted beside it (a
//     bare figure would misread the direction); "square" for a box with nothing past dust; one line per asset.
//   · `more: true` says "Showing the first N" (rosterCut) and nothing claims "none" over it; desk accounts are never listed
//     (partiesOnly); Dormant parties fold behind a true-count Show all (N).
//   · RESTORED 1 Nov 2026 (his "restore most the lost things … with the improved UI"): on the laptop one column per asset the
//     list holds (the first four by the desk's order; the rest in one Other column), so assets compare across parties; the
//     foot says "Net" per asset over the parties shown; Customers / Merchants sit beside the search, not behind Filters.
//
// Behaviour kept from screens/Parties.tsx: the same queries and keys (['parties', type, q, withBalances], pageSize 500),
// the optimistic pin with its rollback and its one error line, compareRelevance + partitionByRelevance, KYC words in full
// (never colour alone), the name's review flag.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus, Star } from 'lucide-react';
import { api, type PartySummary } from '../../lib/api';
import { errorWords, readWords } from '../../lib/errors';
import { collapseTotals, displayAsset } from '../../lib/format';
import { tradeProfileLine } from '../../lib/trade-profile';
import { partyName, partySubtitle, partyKindLabel, needsNameReview, compareRelevance, partitionByRelevance, dustFor } from '../../lib/naming';
import { partiesOnly } from '../../lib/ownAccounts';
import { cutRosterWords, rosterCut } from '../../lib/roster-cut';
import { readState, offlineFailure } from '../../lib/read-state';
import { tg } from '../../lib/telegram';
import { boxParts } from '../../components/BoxLines';
import { Pill } from '../../components/kit';
import {
  PageHead, Panel, Notice, Register, SearchBar, SkeletonRows, EmptyState, StateLine, TAP, type RegisterColumn, OfflineNotice } from '../../components/v2/kit';

const TYPE_FILTERS = ['all', 'customer', 'counterparty'] as const;
type TypeFilter = typeof TYPE_FILTERS[number];
const TYPE_LABEL: Record<TypeFilter, string> = { all: 'All', customer: 'Customers', counterparty: 'Merchants' };

/** Asset order for a party's lines and the foot (screens/Parties.tsx): the desk's usual assets first, any other after. */
const ASSET_ORDER = ['AED', 'INR', 'USD', 'EUR', 'USDT'];
function orderAssets(assets: Iterable<string>): string[] {
  return [...new Set(assets)].sort((a, b) => {
    const ia = ASSET_ORDER.indexOf(a), ib = ASSET_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

const partyHref = (p: PartySummary) => `/parties/${encodeURIComponent(p.slug)}?entity=dubaiotc`;

interface Row { p: PartySummary; dormant: boolean }

export function PartiesV2() {
  const [type, setType] = useState<TypeFilter>('all');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const qc = useQueryClient();
  const trimmedQ = q.trim();

  useEffect(() => { setShowAll(false); }, [type, trimmedQ]);

  const ask = (withBalances: boolean) => () => api.listParties({
    entity: 'dubaiotc',
    type: type === 'all' ? undefined : type,
    q: trimmedQ || undefined,
    with_balances: withBalances || undefined,
    pageSize: 500,
  });
  // THE FAST READ draws the list; THE BALANCES READ (~2.7 s) fills the Balance column behind it.
  const listKey = ['parties', type, trimmedQ, false] as const;
  const balKey = ['parties', type, trimmedQ, true] as const;
  const query = useQuery({ queryKey: listKey, queryFn: ask(false) });
  const balQuery = useQuery({ queryKey: balKey, queryFn: ask(true) });

  const toggleFavorite = useMutation({
    mutationFn: ({ slug, favorite }: { slug: string; favorite: boolean }) => api.favoriteParty(slug, favorite),
    onMutate: async ({ slug, favorite }) => {
      const prev: Array<[typeof listKey | typeof balKey, { parties: PartySummary[] } | undefined]> = [];
      for (const key of [listKey, balKey]) {
        await qc.cancelQueries({ queryKey: key });
        const old = qc.getQueryData<{ parties: PartySummary[] }>(key);
        prev.push([key, old]);
        if (old) qc.setQueryData(key, { ...old, parties: old.parties.map((p: PartySummary) => (p.slug === slug ? { ...p, is_favorite: favorite } : p)) });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, old] of ctx?.prev ?? []) if (old) qc.setQueryData(key, old);
      tg()?.HapticFeedback.notificationOccurred('error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['parties'] }),
  });
  const onPin = (p: PartySummary) => toggleFavorite.mutate({ slug: p.slug, favorite: !p.is_favorite });

  // THE READS THIS SCREEN ASKS (Appendix B): the roster, and the balances inside the second one.
  const roster = readState(query);
  const balances = readState(balQuery, { balances: true });
  const partial = rosterCut(query.data);

  // Desk accounts never listed (lib/ownAccounts.ts). One comparator, one partition; a search reaches every tier.
  const rows = partiesOnly(query.data?.parties ?? []);
  const { shown, hidden } = partitionByRelevance([...rows].sort(compareRelevance), { maxTier: trimmedQ ? 3 : 2 });
  // PINNING CHANGES THE ORDER, NOT THE ROW: pinned first, in the comparator's order, then the rest.
  const ordered: Row[] = [
    ...shown.filter((p) => p.is_favorite).map((p) => ({ p, dormant: false })),
    ...shown.filter((p) => !p.is_favorite).map((p) => ({ p, dormant: false })),
    ...(showAll ? hidden.map((p) => ({ p, dormant: true })) : []),
  ];

  // The balance a row may claim: only what the list carried. No bag = not read ("—"); with the read failed, a bag that came
  // back is shown as read (a partial read keeps what it read) and nothing is concluded from a missing one.
  const balBySlug = new Map((balQuery.data?.parties ?? []).map((x) => [x.slug, x.balances] as const));
  const bagOf = (p: PartySummary): Record<string, number> | null => {
    const b = balBySlug.get(p.slug);
    return b ? collapseTotals(b) : null;
  };

  // THE PIN lives in the name cell (Astra has no pin column): a quiet star after the name, filled when pinned — the only
  // door to pinning, so it stays.
  const nameCell = (p: PartySummary, dormant: boolean) => (
    <span className={`flex min-w-0 items-center gap-1 ${dormant ? 'opacity-70' : ''}`}>
      <span className="min-w-0 break-words font-medium" title={partyName(p)}>{partyName(p)}</span>
      <PinButton party={p} onPin={onPin} />
    </span>
  );

  // THE ROW'S SECOND LINE (Astra: "Pinned · Last activity 12:04"): pinned, what they deal in ("Buyer · MC / Bank transfer",
  // trade_profile — the operator, 27 Oct: "clearly visible"; nothing when it says nothing), then standing and activity —
  // the role has a column of its own on the laptop and a line of its own on the phone. One line, no pill: the list stays light.
  const metaLine = (p: PartySummary) => [p.is_favorite ? 'Pinned' : '', tradeProfileLine(p.trade_profile), partySubtitle(p, { compact: true, kind: false })].filter(Boolean).join(' · ');

  // THE ASSET COLUMNS (laptop): every asset some shown party holds past dust, in the desk's order; four columns, the rest
  // in one Other column (a line each). Only over a read balances answer — while reading or failed, the one Balance column.
  const heldAssets = balances === 'known'
    ? orderAssets(ordered.flatMap(({ p }) => { const b = bagOf(p); return b ? Object.keys(b).filter((a) => Math.abs(b[a] ?? 0) > dustFor(a)) : []; }))
    : [];
  const colAssets = heldAssets.slice(0, heldAssets.length > 5 ? 4 : 5);
  const otherAssets = heldAssets.slice(colAssets.length);
  const pick = (bag: Record<string, number> | null, keep: (a: string) => boolean) => (bag ? Object.fromEntries(Object.entries(bag).filter(([a]) => keep(a))) : null);
  // THE NET over the parties shown, per asset, in the box's words.
  const net: Record<string, number> = {};
  if (balances === 'known') for (const { p } of ordered) for (const [a, v] of Object.entries(bagOf(p) ?? {})) net[a] = (net[a] ?? 0) + v;
  const netAssets = orderAssets(Object.keys(net)).filter((a) => Math.abs(net[a] ?? 0) > dustFor(a));

  const balanceColumns: RegisterColumn<Row>[] = colAssets.length === 0
    ? [{ key: 'balance', head: 'Balance', align: 'right', width: 'w-[19rem]', cell: ({ p }) => <Balance bag={bagOf(p)} read={balances} /> }]
    : [
      ...colAssets.map((a, i): RegisterColumn<Row> => ({
        key: `bal-${a}`, head: displayAsset(a), align: 'right', width: 'w-[8.5rem]',
        // A party square in every asset says "square" once, in the first column — never a row of blanks that reads as unread
        // (Astra, 1 Nov 2026); "—" stays for a balance not read.
        cell: ({ p }) => {
          const bag = bagOf(p);
          if (i === 0 && bag && Object.keys(bag).every((x) => Math.abs(bag[x] ?? 0) <= dustFor(x))) return <span className="text-tg-hint">square</span>;
          return <AssetCell value={bag?.[a] ?? 0} read={!!bag} asset={a} />;
        },
      })),
      ...(otherAssets.length > 0 ? [{ key: 'bal-other', head: 'Other', align: 'right' as const, width: 'w-[11rem]',
        cell: ({ p }: Row) => <Balance bag={pick(bagOf(p), (a) => otherAssets.includes(a))} read={balances} quietSquare /> }] : []),
    ];

  const columns: RegisterColumn<Row>[] = [
    {
      key: 'party', head: 'Party',
      cell: ({ p, dormant }) => (
        <>
          {nameCell(p, dormant)}
          {metaLine(p) && <span className="mt-1 block text-meta text-tg-hint">{metaLine(p)}</span>}
        </>
      ),
    },
    ...balanceColumns,
    { key: 'role', head: 'Role', width: 'w-[6.5rem]', cell: ({ p }) => partyKindLabel(p) },
    { key: 'state', head: 'Status', align: 'right', width: colAssets.length > 0 ? 'w-[8.5rem]' : 'w-[10rem]', cell: ({ p, dormant }) => <span className="inline-flex items-center gap-2"><Attention p={p} dormant={dormant} /><Chevron /></span> },
  ];

  const retry = (
    <button type="button" className={TAP} disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? 'Trying…' : 'Try again'}</button>
  );

  return (
    <>
      <PageHead
        title="Parties"
        inline
        sub="Customers and merchants"
        actions={(
          <Link to="/parties/new" className="btn-primary inline-flex min-h-11 items-center gap-2 px-4 text-body lg:min-h-10">
            <Plus className="h-4 w-4" aria-hidden />New party
          </Link>
        )}
      />

      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Search name"
        extra={(
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Role">
            {TYPE_FILTERS.map((t) => (
              <button key={t} type="button" aria-pressed={type === t} onClick={() => setType(t)} className={`chip max-lg:min-h-11 ${type === t ? 'chip-on' : ''}`}>{TYPE_LABEL[t]}</button>
            ))}
          </div>
        )}
      />

      {/* ONE FAILURE, ONE MESSAGE. The roster's failure owns the page; the balances' failure speaks only over a roster that
          was read; a cut roster and a refused pin each say their own line. */}
      {roster === 'failed' && offlineFailure(query.error) && <OfflineNotice what="the parties" kept={!!query.data} />}
      {roster === 'failed' && !offlineFailure(query.error) && (
        <Notice tone="bad">
          <StateLine tone="bad" more={readWords(query.error)} action={retry}>
            {query.data ? <>Couldn&rsquo;t refresh — showing the last read.</> : <>Couldn&rsquo;t read the parties.</>}
          </StateLine>
        </Notice>
      )}
      {roster !== 'failed' && balances === 'failed' && (
        <p className="mb-4 text-meta text-tg-hint">
          Balances not read ·{' '}
          <button type="button" className={TAP} disabled={balQuery.isFetching} onClick={() => balQuery.refetch()}>{balQuery.isFetching ? 'Trying…' : 'Try again'}</button>
        </p>
      )}
      {roster !== 'failed' && partial && <Notice tone="neutral">{cutRosterWords(query.data?.parties.length ?? rows.length, 'search')}</Notice>}
      {toggleFavorite.isError && (
        <Notice tone="bad">
          <StateLine tone="bad" more={errorWords(toggleFavorite.error)}>Couldn&rsquo;t change the pin.</StateLine>
        </Notice>
      )}

      {roster === 'waiting' && !query.data ? (
        <Panel flush><SkeletonRows rows={6} what="parties" /></Panel>
      ) : ordered.length > 0 ? (
        <Panel flush>
          <Register
            label="Parties"
            columns={columns}
            rows={ordered}
            rowKey={({ p }) => p.slug}
            href={({ p }) => partyHref(p)}
            card={({ p, dormant }) => ({
              title: (
                <>
                  {/* the card's full width, so every balance line ends on one right edge (Astra, 1 Nov 2026) */}
                  <span aria-hidden className="block h-0 w-screen max-w-full" />
                  {nameCell(p, dormant)}
                  {metaLine(p) && <span className="mt-1 block text-meta font-normal text-tg-hint">{metaLine(p)}</span>}
                  <Balance bag={bagOf(p)} read={balances} phone />
                </>
              ),
              meta: partyKindLabel(p),
              state: <span className="inline-flex items-center gap-2"><Attention p={p} dormant={dormant} /><Chevron /></span>,
            })}
          />
          {/* Astra's foot. The count only over a whole read (a cut roster says "Showing the first N" above instead). */}
          <div className="border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span className="block">{roster === 'known' && !partial ? `${ordered.length} ${ordered.length === 1 ? 'party' : 'parties'}` : ''}</span>
            {/* THE NET over the parties shown — only over a whole roster and a read balances answer — as labelled cells (his
                "one fact per place", 1 Nov 2026: it was one sentence of four totals): the asset and the box's word over the figure. */}
            {roster === 'known' && !partial && netAssets.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-8 gap-y-2" aria-label="Net over the parties shown">
                {netAssets.map((a) => {
                  const { words, figure } = boxParts(net[a] ?? 0, a);
                  return (
                    <div key={a} className="min-w-0">
                      <div className="text-label font-medium uppercase tracking-wider">Net {displayAsset(a)} · {words}</div>
                      <div className={`mt-0.5 whitespace-nowrap font-mono tabular text-body font-medium ${(net[a] ?? 0) < 0 ? 'text-neg' : 'text-tg-text'}`}>{figure}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Panel>
      ) : roster === 'known' && rows.length === 0 && !partial ? (
        // AN EMPTY CLAIM NEEDS A SUCCESSFUL, WHOLE READ (Appendix B).
        <EmptyState>{trimmedQ ? 'Nothing here.' : 'No parties yet.'}</EmptyState>
      ) : null}

      {/* Dormant parties are folded, never dropped — and the count is real. */}
      {hidden.length > 0 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className={`${TAP} mt-3 inline-block`}>
          {showAll ? 'Show fewer' : `${hidden.length} more →`}
        </button>
      )}
    </>
  );
}

/** THE PIN: order, not anatomy. A button beside the row's link (it never opens the party). */
function PinButton({ party, onPin }: { party: PartySummary; onPin: (p: PartySummary) => void }) {
  const fav = !!party.is_favorite;
  const label = fav ? 'Unpin' : 'Pin';
  return (
    <button
      type="button"
      aria-pressed={fav}
      aria-label={`${label} ${partyName(party)}`}
      title={label}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPin(party); }}
      className="-my-3 inline-grid h-11 w-11 shrink-0 place-items-center rounded-lg hover:bg-panel-2 lg:h-10 lg:w-10"
    >
      <Star className={`h-3.5 w-3.5 ${fav ? 'fill-current text-tg-accent' : 'text-tg-hint opacity-50'}`} aria-hidden />
    </button>
  );
}

/** What needs him about this party, as Astra's status word: KYC pending / rejected, Needs review (the stored name), Dormant.
 *  Nothing for an ordinary party — the list read carries no status, so it claims none. */
function Attention({ p, dormant }: { p: PartySummary; dormant: boolean }) {
  if (p.kyc_status === 'pending') return <Pill tone="warn">KYC pending</Pill>;
  if (p.kyc_status === 'rejected') return <Pill tone="bad">KYC rejected</Pill>;
  if (needsNameReview(p.display_name)) return <Pill tone="warn">Needs review</Pill>;
  if (dormant) return <Pill tone="neutral">Dormant</Pill>;
  return null;
}

/** THE BALANCE CELL — Astra's figure (the amount and its asset, right-aligned) with the box's word muted beside it, one line
 *  per asset. Waiting: "…" (never 0). Failed, or no bag for this party: "—". A bag with nothing past dust: "square" — only
 *  over a read the BFF called whole (`balances_read: true`; readState 'known'). */
/** ONE ASSET'S CELL (laptop columns): the figure, the box's word under it in the meta size; nothing for a square box (the
 *  column's head names the asset, and a column of "square" is noise); "—" when no balance came back. */
function AssetCell({ value, asset, read }: { value: number; asset: string; read: boolean }) {
  if (!read) return <span className="text-tg-hint">—</span>;
  const { words, figure } = boxParts(value, asset);
  if (!figure) return <span className="sr-only">square</span>;
  return (
    <span className="block whitespace-nowrap">
      <span className={`block font-mono tabular font-medium ${value < 0 ? 'text-neg' : ''}`}>{figure}</span>
      <span className="block text-meta text-tg-hint">{words}</span>
    </span>
  );
}

function Balance({ bag, read, phone = false, quietSquare = false }: { bag: Record<string, number> | null; read: 'known' | 'waiting' | 'failed'; phone?: boolean; quietSquare?: boolean }) {
  const wrap = phone ? 'mt-2 space-y-1 text-body font-normal' : 'space-y-1';
  if (read === 'waiting') return <div className={wrap}><span className="text-tg-hint" aria-label="Reading the balance">…</span></div>;
  if (read === 'failed' || !bag) return <div className={wrap}><span className="text-tg-hint">—</span></div>;
  const lines = orderAssets(Object.keys(bag)).filter((a) => Math.abs(bag[a] ?? 0) > dustFor(a));
  if (lines.length === 0) return <div className={wrap}><span className="text-tg-hint">{quietSquare ? '—' : 'square'}</span></div>;
  return (
    <div className={wrap}>
      {lines.map((a) => {
        const { words, figure } = boxParts(bag[a] ?? 0, a);
        return (
          // The box's word on the left, the figure and its unit on the right: every line's figure on one numeric edge (the
          // phone's lines started wherever the word ended — the audit, 1 Nov 2026).
          <div key={a} className="flex items-baseline justify-between gap-3 whitespace-nowrap">
            <span className="text-meta text-tg-hint">{words}</span>
            <span><span className={`font-mono tabular font-medium ${(bag[a] ?? 0) < 0 ? 'text-neg' : ''}`}>{figure}</span>{' '}
              <span className="text-meta text-tg-hint">{displayAsset(a)}</span></span>
          </div>
        );
      })}
    </div>
  );
}

function Chevron() {
  return <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
}
